import { useState } from 'react';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_secure-parcels/artifacts/gsdtwsrt_ISOTIPO.png";

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: 'Olá! 👋 Sou o assistente virtual do Use Lokker. Como posso ajudar você hoje?',
    },
  ]);
  const [inputValue, setInputValue] = useState('');

  const quickReplies = [
    'Como funciona o Use Lokker?',
    'Quais são os planos disponíveis?',
    'Onde encontro um locker?',
    'Falar com atendente',
  ];

  const handleSendMessage = (text) => {
    if (!text.trim()) return;

    const newMessage = {
      id: messages.length + 1,
      type: 'user',
      text: text,
    };

    setMessages([...messages, newMessage]);
    setInputValue('');

    // Simulated bot response
    setTimeout(() => {
      let botResponse = '';
      
      if (text.toLowerCase().includes('funciona')) {
        botResponse = 'O Use Lokker funciona em 4 passos simples: 1️⃣ Faça seu cadastro, 2️⃣ Receba suas encomendas no locker, 3️⃣ Receba a notificação no celular, 4️⃣ Retire quando quiser! Quer saber mais?';
      } else if (text.toLowerCase().includes('plano')) {
        botResponse = 'Temos dois planos para pessoa física: 💳 Plano Por Uso (cadastro grátis, paga só quando usar) e 📅 Plano Assinatura (R$ 59,90/mês com uso ilimitado). Qual te interessa mais?';
      } else if (text.toLowerCase().includes('locker') || text.toLowerCase().includes('onde')) {
        botResponse = 'Você pode encontrar nossos lockers em diversos pontos da cidade! Acesse nosso mapa interativo em /mapa para ver todos os locais disponíveis perto de você. 📍';
      } else if (text.toLowerCase().includes('atendente') || text.toLowerCase().includes('humano')) {
        botResponse = 'Claro! Um de nossos atendentes entrará em contato em breve. Enquanto isso, posso ajudar com alguma outra dúvida? 😊';
      } else {
        botResponse = 'Entendi! Para melhor atendê-lo, você pode escolher uma das opções abaixo ou digitar sua dúvida específica. Estou aqui para ajudar! 🚀';
      }

      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          type: 'bot',
          text: botResponse,
        },
      ]);
    }, 1000);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        data-testid="chat-widget-button"
        className={`fixed bottom-24 right-6 z-[9999] w-14 h-14 bg-lokker-blue rounded-full flex items-center justify-center shadow-lg hover:bg-lokker-blue-dark transition-all duration-300 ${
          isOpen ? 'scale-0' : 'scale-100'
        }`}
      >
        <MessageCircle className="w-6 h-6 text-white" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-lokker-lime rounded-full animate-pulse-soft" />
      </button>

      {/* Chat Window */}
      <div
        data-testid="chat-widget-window"
        className={`fixed bottom-24 right-6 z-[9999] w-[360px] max-w-[calc(100vw-48px)] bg-white rounded-sm shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
        }`}
      >
        {/* Header */}
        <div className="bg-lokker-blue px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center p-1">
              <img src={LOGO_ICON} alt="Lokker" className="w-full h-full" />
            </div>
            <div>
              <h4 className="font-heading font-semibold text-white text-sm">Assistente Lokker</h4>
              <p className="text-lokker-lime text-xs">Online agora</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            data-testid="chat-close-button"
            className="text-white/70 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="h-[320px] overflow-y-auto p-4 space-y-4 bg-slate-50">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-2 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.type === 'bot' && (
                <div className="w-8 h-8 bg-lokker-blue rounded-full flex items-center justify-center flex-shrink-0 p-1">
                  <img src={LOGO_ICON} alt="" className="w-full h-full" />
                </div>
              )}
              <div
                className={`max-w-[75%] px-4 py-2 rounded-sm ${
                  message.type === 'user'
                    ? 'bg-lokker-blue text-white'
                    : 'bg-white text-slate-700 border border-slate-200'
                }`}
              >
                <p className="text-sm leading-relaxed">{message.text}</p>
              </div>
              {message.type === 'user' && (
                <div className="w-8 h-8 bg-slate-300 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-slate-600" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Quick Replies */}
        <div className="px-4 py-2 border-t border-slate-200 bg-white">
          <div className="flex flex-wrap gap-2">
            {quickReplies.map((reply, index) => (
              <button
                key={index}
                onClick={() => handleSendMessage(reply)}
                data-testid={`quick-reply-${index}`}
                className="text-xs px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full hover:bg-lokker-lime hover:text-black transition-colors"
              >
                {reply}
              </button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-slate-200 bg-white">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage(inputValue);
            }}
            className="flex gap-2"
          >
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Digite sua mensagem..."
              data-testid="chat-input"
              className="flex-1 bg-slate-50 border-slate-200 focus:border-lokker-blue rounded-sm text-sm"
            />
            <Button
              type="submit"
              data-testid="chat-send-button"
              className="bg-lokker-blue hover:bg-lokker-blue-dark text-white rounded-sm px-3"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
